import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { authStoreUserSelector } from 'modules/auth/store/selectors';
import { receiptEmailPrefill, SBP_MIN_AMOUNT_RUBLES } from 'modules/organizer/domain/constants/wallet-topup';
import { SUBSCRIPTION_EXPIRY_NOTICE } from 'modules/organizer/domain/constants/subscription-expiry-notice';
import { formatRublesFromMinorUnits, rublesToMinorUnits } from 'modules/organizer/domain/utils/money';
import { formatSubscriptionExpiryMessage } from 'modules/organizer/domain/utils/subscription-expiry-notice';
import { BuyPlanFormValues, WalletTopupFormValues } from 'modules/organizer/domain/validators/wallet-topup.validator';
import { useSubscriptionBilling } from 'modules/organizer/ui/hooks/use-subscription-billing';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React from 'react';
import { theme } from 'styles/theme/theme';
import UDCard from 'ud-ui/components/card';
import UDLink from 'ud-ui/components/link';
import UDText from 'ud-ui/components/text';
import { SubscriptionBuyPlanForm } from './subscription-buy-plan-form';
import {
  SUBSCRIPTION_BADGE_CLASS,
  SUBSCRIPTION_COLUMN_CLASS,
  SUBSCRIPTION_COPY,
  SUBSCRIPTION_HEADER_CLASS,
  SUBSCRIPTION_INFO_ROW_CLASS,
} from './subscription-layout';
import { SubscriptionQrStep } from './subscription-qr-step';
import { SubscriptionSkeleton } from './subscription-skeleton';
import { SubscriptionTopupForm } from './subscription-topup-form';
import { SubscriptionTopupSuccess } from './subscription-topup-success';
import { useAppSelector } from 'store/hooks';

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={SUBSCRIPTION_INFO_ROW_CLASS}>
      <UDText type="footnote" color={theme.colors.Gray}>
        {label}
      </UDText>
      <UDText type="subhead">{value}</UDText>
    </div>
  );
}

function Subscription() {
  const user = useAppSelector(authStoreUserSelector);
  const {
    tournamentId,
    wallet,
    plans,
    activePlan,
    defaultPlan,
    pendingTopup,
    isLoading,
    hasSubscription,
    planCount,
    isCreating,
    isBuying,
    submitError,
    buyError,
    startTopup,
    payPlan,
    resetTopup,
  } = useSubscriptionBilling();

  if (!tournamentId) {
    return (
      <UDCard className="max-w-md">
        <UDText type="headline" className="mb-2">
          Выберите организатора
        </UDText>
        <UDText type="body-text" color={theme.colors.Gray} className="mb-3">
          Чтобы пополнить баланс и оплатить тариф, сначала выберите организацию.
        </UDText>
        <UDLink to={Pages.SELECT_TOURNAMENT}>Перейти к выбору</UDLink>
      </UDCard>
    );
  }

  if (isLoading) {
    return <SubscriptionSkeleton hasSubscription={hasSubscription} planCount={planCount} />;
  }

  const subscription = activePlan?.subscription ?? null;
  const daysLeft = subscription ? differenceInCalendarDays(parseISO(subscription.activeTo), new Date()) : null;
  const isExpiringSoon =
    daysLeft !== null && daysLeft >= 0 && daysLeft <= SUBSCRIPTION_EXPIRY_NOTICE.warnWithinDays;
  const planName = activePlan?.name ?? null;
  // Пополнить предлагаем только недостающее до тарифа: поле принимает целые рубли, поэтому остаток — вверх.
  const shortfallInMinorUnits = defaultPlan ? Math.max(defaultPlan.priceInMinorUnits - (wallet?.balanceInMinorUnits ?? 0), 0) : 0;
  const suggestedTopup = Math.max(Math.ceil(shortfallInMinorUnits / 100), SBP_MIN_AMOUNT_RUBLES);
  const isQrFlow = !!pendingTopup && pendingTopup.status !== 'CONFIRMED';

  const handleTopup = async (values: WalletTopupFormValues) => {
    await startTopup({
      amountInMinorUnits: rublesToMinorUnits(values.amount),
      paymentMethod: values.paymentMethod,
      email: values.email,
    });
  };

  const handleBuyPlan = async (values: BuyPlanFormValues) => {
    await payPlan({ planId: values.planId });
  };

  return (
    <div className={SUBSCRIPTION_COLUMN_CLASS}>
      <UDCard>
        <UDText type="headline" className="mb-2">
          {SUBSCRIPTION_COPY.balanceTitle}
        </UDText>
        <UDText type="title-1">{formatRublesFromMinorUnits(wallet?.balanceInMinorUnits ?? 0)} ₽</UDText>
      </UDCard>

      <UDCard>
        {subscription ? (
          <>
            <div className={SUBSCRIPTION_HEADER_CLASS}>
              <UDText className="h-6" type="headline">
                {SUBSCRIPTION_COPY.activeTitle}
              </UDText>
              <span className={`${SUBSCRIPTION_BADGE_CLASS} text-white ${isExpiringSoon ? 'bg-error' : 'bg-darkYellow-500'}`}>
                {isExpiringSoon ? SUBSCRIPTION_COPY.expiringBadge : SUBSCRIPTION_COPY.activeBadge}
              </span>
            </div>
            <InfoRow label={SUBSCRIPTION_COPY.activeFrom} value={format(parseISO(subscription.activeFrom), 'dd.MM.yyyy')} />
            <InfoRow label={SUBSCRIPTION_COPY.activeTo} value={format(parseISO(subscription.activeTo), 'dd.MM.yyyy')} />
            <InfoRow label={SUBSCRIPTION_COPY.plan} value={planName ?? subscription.purchaseType} />
            {isExpiringSoon && (
              <UDText type="footnote" className="text-error mt-1 block">
                {formatSubscriptionExpiryMessage(daysLeft ?? 0, subscription.activeTo)}
              </UDText>
            )}
          </>
        ) : (
          <>
            <UDText type="headline" className="mb-2">
              {SUBSCRIPTION_COPY.inactiveTitle}
            </UDText>
            <UDText type="body-text" color={theme.colors.Gray}>
              {SUBSCRIPTION_COPY.inactiveHint}
            </UDText>
          </>
        )}
      </UDCard>

      <UDCard>
        {pendingTopup?.status === 'CONFIRMED' ? (
          <SubscriptionTopupSuccess onDone={resetTopup} />
        ) : pendingTopup ? (
          <SubscriptionQrStep topup={pendingTopup} onRetry={resetTopup} />
        ) : (
          <SubscriptionTopupForm
            initialAmount={suggestedTopup}
            initialEmail={receiptEmailPrefill(user?.email)}
            isSubmitting={isCreating}
            error={submitError}
            onSubmit={handleTopup}
          />
        )}
      </UDCard>

      {!subscription && !isQrFlow && (
        <UDCard>
          <SubscriptionBuyPlanForm
            plans={plans}
            initialPlanId={defaultPlan?.id ?? 0}
            balanceInMinorUnits={wallet?.balanceInMinorUnits ?? 0}
            isSubmitting={isBuying}
            error={buyError}
            onSubmit={handleBuyPlan}
          />
        </UDCard>
      )}
    </div>
  );
}

export default Subscription;
